import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITIES_QUERY_KEY } from './useGetCommunities';
import { QUERY_KEY as GET_COMMUNITY_QUERY_KEY } from './useGetCommunity';

export type ApiRequest_Update_Community_Settings = {
  communityId: number;
  name?: string;
  description?: string;
  require_verification?: boolean;
  is_active?: boolean;
};

export type ApiResponse_Update_Community_Settings = {
  id: number;
  name: string;
  description: string;
  rules: string | null;
  is_active: boolean;
  require_verification: boolean;
  created_at: string;
  updated_at: string;
};

const METHOD = 'PUT';
const ENDPOINT = 'communities/{communityId}/settings';

export const useUpdateCommunitySettings = (options?: {
  onSuccess?: (data: ApiResponse_Update_Community_Settings) => void;
  onError?: (error: Error) => void;
}) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Update_Community_Settings) => {
      try {
        const { communityId, ...settingsData } = params;
        
        const data = await handleApiCall<
          ApiResponse_Update_Community_Settings,
          Omit<ApiRequest_Update_Community_Settings, 'communityId'>
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { communityId: communityId.toString() }),
          settingsData
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to update community settings.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data) => {
      enqueueSnackbar('Community settings updated successfully!', { variant: 'success' });
      
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: GET_COMMUNITY_QUERY_KEY({ id: data.id }) 
      });
      queryClient.invalidateQueries({ 
        queryKey: GET_COMMUNITIES_QUERY_KEY() 
      });
      
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};